"use client";

import { motion } from "framer-motion";
import { PortableText } from "next-sanity";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { PagePricingSchema } from "@/sanity/pages/PagePricingTemplate";
import { CTAButton } from "@/shadcn/ui/button";
import { GroqImage } from "@/helpers/Image";

const ease = [0.25, 0.1, 0.25, 1] as const;

function IQLogo() {
  return (
    <div className="bg-black flex items-center justify-center rounded-[0.25rem] w-[4.875rem] h-[4.875rem] flex-shrink-0">
      <svg
        width="32"
        height="32"
        viewBox="0 0 64 64"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path d="M14 44.4223V17.5979H20.5559V44.4223H14Z" fill="white" />
        <path
          d="M42.9192 46.6484L40.8837 44.1555C40.3335 44.4315 39.6367 44.643 38.8115 44.7994C37.9862 44.9466 37.0693 45.0294 36.0607 45.0294C33.4017 45.0294 31.0727 44.4407 29.0922 43.2724C27.1116 42.1041 25.5804 40.4575 24.4984 38.3417C23.4165 36.2259 22.8755 33.7974 22.8755 31.0469C22.8755 28.2964 23.3798 25.9874 24.3792 23.8717C25.3787 21.7559 26.8732 20.0817 28.8446 18.849C30.816 17.6163 33.2183 17 36.0607 17C38.9031 17 41.2963 17.6163 43.2493 18.849C45.2115 20.0817 46.6877 21.7559 47.6964 23.8717C48.6958 25.9874 49.2001 28.3792 49.2001 31.0469C49.2001 33.5398 48.7875 35.7016 47.9531 37.523C47.1279 39.3536 45.9542 40.8898 44.4505 42.1501L48.0631 46.6484H42.9009H42.9192ZM36.0699 39.6572C36.345 39.6572 36.6017 39.6388 36.8217 39.602C37.051 39.5652 37.2619 39.51 37.4636 39.4272L33.8143 34.9657H38.9765L40.6361 37.155C41.2138 36.5295 41.6631 35.7292 41.9931 34.7541C42.3232 33.7882 42.4791 32.5463 42.4791 31.0285C42.4791 29.5107 42.2591 28.0664 41.8189 26.7602C41.3788 25.4539 40.6911 24.3868 39.7467 23.5681C38.8023 22.7494 37.5828 22.3446 36.0791 22.3446C34.5753 22.3446 33.31 22.7586 32.3655 23.5681C31.4211 24.3868 30.7334 25.4539 30.2933 26.7602C29.8532 28.0664 29.6331 29.4923 29.6331 31.0285C29.6331 32.5647 29.8624 33.9538 30.3117 35.26C30.7609 36.5663 31.4578 37.6242 32.4022 38.4337C33.3466 39.2432 34.5661 39.6388 36.0699 39.6388V39.6572Z"
          fill="white"
        />
      </svg>
    </div>
  );
}

export default function GettingStartedSection({
  data,
}: {
  data: PagePricingSchema;
}) {
  const steps = data?.gettingStartedSteps ?? [];

  if (!steps.length && !data?.gettingStartedTitle) return null;

  return (
    <section className="relative w-full bg-white py-[10rem] lg:py-[20rem]">
      <div className="mx-auto w-[min(112.5rem,calc(100%-4rem))] lg:w-[min(112.5rem,calc(100%-8rem))] flex flex-col gap-[8rem]">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8, ease }}
          className="flex flex-col items-center gap-[2rem] text-center"
        >
          {data?.gettingStartedChip && (
            <p className="mb-0 font-blacker italic text-[2rem] leading-[1.5] text-black">
              {data.gettingStartedChip}
            </p>
          )}
          {data?.gettingStartedTitle && (
            <div className="flex flex-col items-center gap-[1.5rem]">
              <div className="flex items-center gap-[1.5rem]">
                <IQLogo />
                <div className="richtext richtext-start prose-headings:text-[4.25rem] lg:prose-headings:text-[6rem] prose-headings:font-medium prose-headings:leading-[1.15] prose-headings:tracking-[-0.3rem] prose-headings:text-black prose-em:font-blacker prose-em:italic prose-em:text-[#003828] [&_p]:hidden">
                  <PortableText
                    value={data.gettingStartedTitle}
                    components={richTextComponent}
                  />
                </div>
              </div>
              <div className="richtext prose-p:text-black/70 prose-p:text-[2rem] prose-headings:hidden max-w-[56rem]">
                <PortableText
                  value={data.gettingStartedTitle}
                  components={richTextComponent}
                />
              </div>
            </div>
          )}
        </motion.div>

        {/* Cards */}
        {!!steps.length && (
          <div className="flex flex-col gap-[2rem]">
            {steps.map((step, index) => {
              const num = String(index + 1).padStart(2, "0");
              return (
                <motion.div
                  key={step._key ?? index}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.15 }}
                  transition={{ duration: 0.7, ease, delay: index * 0.07 }}
                  className="flex flex-col md:flex-row md:items-stretch gap-[2.5rem] md:gap-[3rem] border border-[rgba(0,0,0,0.1)] rounded-[0.25rem] overflow-hidden p-[3rem] md:p-[3.5rem]"
                >
                  <div className="flex flex-col gap-[1.75rem] flex-1 justify-center md:px-[5rem]">
                    <div className="bg-[#18634F] border border-[rgba(140,150,165,0.1)] px-[1.5rem] h-[3.332rem] flex items-center justify-center rounded-[0.25rem] w-fit">
                      <p className="mb-0 font-geist-mono font-medium text-[2rem] text-white uppercase">
                        {num}
                      </p>
                    </div>
                    {step.title && (
                      <h3 className="mb-0 text-[3rem] md:text-[3.25rem] font-medium leading-[1.16] tracking-[-0.163rem] text-[#2e3436]">
                        {step.title}
                      </h3>
                    )}
                    {step.body && (
                      <div className="richtext richtext-start prose-p:text-[#1c1f22] prose-p:text-[2rem] md:prose-p:text-[2.25rem]">
                        <PortableText
                          value={step.body}
                          components={richTextComponent}
                        />
                      </div>
                    )}
                  </div>

                  {step.image && (
                    <div className="relative w-full h-[35rem] md:w-[41.25rem] md:h-[32.375rem] flex-shrink-0 overflow-hidden rounded-[0.25rem]">
                      <GroqImage
                        src={step.image}
                        alt={step.title ?? ""}
                        className="!absolute !inset-0 !w-full !h-full !object-cover"
                        quality={100}
                      />
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        )}

        {data?.gettingStartedButton && (
          <div className="flex justify-center">
            <div className="w-full lg:w-fit">
              <CTAButton data={data.gettingStartedButton} />
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
